/**
 * Form-specific patterns — label/input stacks, validation, checkbox lists, info notes.
 * Plain objects for inline `style={}`. Import from `src/ui` in new code.
 */

import { foundation as f } from './tokens';
import { components } from './theme/componentTokens';
import { semantic as sem } from './theme/semanticTokens';

const { color, spacePx, radii, fontSize, fontWeight, lineHeight, fontFamily } = f;

/** Label + input stacked column with gap */
export const formFieldGroup = {
  display: 'flex',
  flexDirection: 'column',
  gap: spacePx[2],
};

/** Responsive 2-column grid for side-by-side form fields */
export const formFieldRow = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: spacePx[4],
};

/** Field label typography */
export const formLabel = {
  fontFamily: fontFamily.sans,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.medium,
  color: color.neutral.text,
  lineHeight: lineHeight.tight,
};

/** Inline field validation error text */
export const formErrorText = {
  fontFamily: fontFamily.sans,
  fontSize: fontSize.xs,
  color: color.semantic.danger,
  margin: `${spacePx[1]} 0 0 0`,
  lineHeight: lineHeight.tight,
};

/** Action button row at bottom of forms — right-aligned */
export const formActionsRow = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: spacePx[2],
  marginTop: spacePx[4],
  flexWrap: 'wrap',
};

/** Blue-tinted informational note box */
export const infoNoteBox = {
  backgroundColor: color.neutral.bgSubtle,
  border: `1px solid ${color.semantic.infoBorder}`,
  borderRadius: radii.xl,
  padding: spacePx[4],
  color: color.semantic.info,
  fontFamily: fontFamily.sans,
  fontSize: fontSize.sm,
  lineHeight: lineHeight.relaxed,
};

/** Selection list container */
export const checkboxList = {
  display: 'flex',
  flexDirection: 'column',
  gap: spacePx[2],
};

/** Selectable row with hover state */
export const checkboxItem = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: spacePx[4],
  backgroundColor: color.neutral.bgSubtle,
  borderRadius: radii.xl,
  cursor: 'pointer',
  border: '2px solid transparent',
  transition: 'background-color 0.15s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.15s cubic-bezier(0.16, 1, 0.3, 1)',
};

/** Selected state override for checkboxItem */
export const checkboxItemSelected = {
  backgroundColor: sem.tint.indigo50,
  border: `2px solid ${sem.edge.navy}`,
};

/** Icon + label flex row inside a checkbox item (left side) */
export const checkboxItemLeft = {
  display: 'flex',
  alignItems: 'center',
  gap: spacePx[3],
};

/** Custom square checkbox control */
export const customCheckbox = {
  width: '24px',
  height: '24px',
  borderRadius: radii.sm,
  border: `2px solid ${color.neutral.borderStrong}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: fontFamily.sans,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.bold,
  color: color.brand.onPrimary,
  flexShrink: 0,
  transition: 'background-color 0.15s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.15s cubic-bezier(0.16, 1, 0.3, 1)',
};

/** Checked state override for customCheckbox */
export const customCheckboxChecked = {
  backgroundColor: color.brand.primary,
  border: `2px solid ${color.brand.primary}`,
};

export const focusRingStyle = {
  borderColor: components.input.focusBorder,
  boxShadow: components.input.focusOutline,
  outline: 'none',
};
